import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { WINDOW, WindowService } from '@campus/browser';
import { DcrDialogService } from '@campus/components';
import {
  ContentInterface,
  DalState,
  EduContent,
  EduContentApi,
  EduContentTypeEnum,
  EduFile,
  EduFileService,
  EffectFeedback,
  EffectFeedbackActions,
  Link,
  Permissions,
  UserContent,
} from '@campus/dal';
import {
  ENVIRONMENT_API_TOKEN,
  ENVIRONMENT_WEBSITE_TOKEN,
  EnvironmentApiInterface,
  EnvironmentWebsiteInterface,
} from '@campus/environment';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { PermissionService } from '../../auth/permission.service';
import { DcrContentPreviewDialogComponent } from '../../components/content-preview-dialog/dcr-content-preview-dialog.component';
import { TextMappingPipe } from '../../pipes';
import { LinkService } from '../link';
import { MathJaxService } from '../mathjax';
import { OpenStaticContentOptions, OpenStaticContentServiceInterface } from './open-static-content.interface';

import '@diekeure/dcr-components/dcr-dialog.js';
import '@diekeure/dcr-components/dcr-inline-button.js';

@Injectable({
  providedIn: 'root',
})
export class OpenStaticContentService implements OpenStaticContentServiceInterface, OnDestroy {
  private mathjaxService = inject(MathJaxService);
  private dcrDialogService = inject(DcrDialogService);
  private permissionService = inject(PermissionService);
  private eduContentApi = inject(EduContentApi);
  private eduFileService = inject(EduFileService);
  private linkService = inject(LinkService);
  private store = inject<Store<DalState>>(Store);
  private textMappingPipe = inject(TextMappingPipe);
  private windowService = inject(WindowService);
  private window = inject<Window>(WINDOW);
  private environmentApi = inject<EnvironmentApiInterface>(ENVIRONMENT_API_TOKEN);
  private uuid = inject('uuid' as any) as () => string;
  private environmentWebsite = inject<EnvironmentWebsiteInterface>(ENVIRONMENT_WEBSITE_TOKEN);
  private canPrintRichText = toSignal(
    this.permissionService.hasPermission$([Permissions.feature.CAN_PRINT_RICH_TEXT]),
    { initialValue: false }
  );
  private subscriptions = new Subscription();
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Opens any object that implements ContentInterface
   *
   * @param content The content that is being opened
   * @param {OpenStaticContentOptions} options OpenStaticContentOptions
   *
   */
  open(content: ContentInterface, options: OpenStaticContentOptions = {}): void {
    if (content instanceof UserContent) return this.handleUserContent(content);
    if (content instanceof EduContent) return this.handleEduContent(content, options);
    if (content instanceof EduFile) return this.handleEduFile(content);
    if (content instanceof Link) return this.handleLink(content);
  }

  private showPreview(options: OpenStaticContentOptions, content: EduContent, url: string) {
    const showPrintButton = options.canPrint;
    const printUrl = this.buildPrintUrl(content.id, options.context?.bookId);

    const dialogConfig = showPrintButton
      ? { type: 'confirm-cancel' as const, confirmLabel: 'Afdrukken', cancelLabel: 'Sluiten' }
      : { type: 'acknowledge' as const, acknowledgeLabel: 'Sluiten' };

    if (options.disableMathjax || !content.isRichText) {
      const data = content.isRichText
        ? { html: content.data, title: content.name, printUrl, ...(showPrintButton && { showPrintButton }) }
        : { url, printUrl, ...(showPrintButton && { showPrintButton }) };

      this.dcrDialogService
        .open(DcrContentPreviewDialogComponent, {
          headline: content.name ?? 'Voorbeeld',
          ...dialogConfig,
          fullscreen: options.fullscreen ?? false,
          data,
        })
        .then((result) => {
          if (result.action === 'confirm' && result.data?.showPrintButton === true)
            this.window.open(printUrl, '_blank');
        });
    } else {
      this.subscriptions.add(
        this.mathjaxService
          .ready()
          .pipe(switchMap(() => this.mathjaxService.render(content.data)))
          .subscribe((html) => {
            this.dcrDialogService
              .open(DcrContentPreviewDialogComponent, {
                headline: content.name ?? 'Voorbeeld',
                ...dialogConfig,
                fullscreen: options.fullscreen ?? false,
                data: { html, title: content.name, printUrl, ...(showPrintButton && { showPrintButton }) },
              })
              .then((result) => {
                if (result.action === 'confirm' && result.data?.showPrintButton === true)
                  this.window.open(printUrl, '_blank');
              });
          })
      );
    }
  }

  private apiRedirect(content: EduContent, contentId: number, options: OpenStaticContentOptions) {
    const isGame =
      content.type === EduContentTypeEnum.WEB_APP && content.publishedEduContentMetadata?.fileExt === 'gamehoek';
    const target = isGame ? '_campusstaticcontent' : undefined;

    const openedWindow = this.window.open(this.environmentWebsite.loadingUrl || '', target);
    this.eduContentApi.openStaticContentUrl(contentId, options).subscribe({
      next: (redirect) => {
        //give absolute links here because it can be opened from the ink
        if (redirect.url?.startsWith('/')) {
          redirect.url = this.environmentApi.APIBase + redirect.url;
        }

        return this.handleRedirect(openedWindow, redirect.url);
      },
      error: (err: HttpErrorResponse) => this.handleError(openedWindow, err),
    });
  }

  private handleUserContent(content: UserContent): void {
    this.window.open(content.link);
  }

  private handleEduContent(content: EduContent, options: OpenStaticContentOptions): void {
    // Speech content is opened by SpeechExerciseService (EXERCISE_TOKEN chain).
    if (content.type === EduContentTypeEnum.SPEECH) {
      return;
    }

    const contentId = content.id;
    if (contentId === undefined) return;

    const url = this.getUrl(contentId, options);

    if (options.showPreview) {
      return this.showPreview(options, content, url);
    }

    if (options.useIFrame) {
      this.windowService.openWindow({ name: null, url, useIframe: options.useIFrame, iframeAllowList: [] });
      return;
    }

    this.apiRedirect(content, contentId, options);
  }

  private handleEduFile(eduFile: EduFile): void {
    this.eduFileService.downloadEduFile(eduFile.id);
  }

  private handleLink(link: Link): void {
    this.linkService.handleLink(link);
  }

  private getUrl(contentId: number, options: OpenStaticContentOptions): string {
    const path = this.eduContentApi.getStaticContentPath(contentId, options);
    return `${this.environmentApi.APIBase}${path}`;
  }

  private buildPrintUrl(contentId: number, bookId?: number): string {
    const base = `${this.environmentApi.APIBase}/api/EduContents/${contentId}/print-pdf`;
    return bookId ? `${base}?bookId=${bookId}` : base;
  }

  private handleRedirect(openedWindow: Window | null, url: string) {
    if (!url) {
      return this.handleError(openedWindow, new HttpErrorResponse({ status: 404 }));
    }

    if (!openedWindow || openedWindow.closed) {
      return;
    }

    openedWindow.location.replace(url);
  }

  private handleError(openedWindow: Window | null, err: HttpErrorResponse) {
    const errorMessage =
      err.status === 401
        ? 'Uw licentie voor dit lesmateriaal is verlopen. ' +
          `Vraag aan uw ${this.textMappingPipe.transform('common.schooladmin')} om de licentie te vernieuwen.`
        : 'Er was een probleem bij het openen van deze oefening. Als het probleem blijft aanhouden, contacteer de helpdesk.';

    const effectFeedback = EffectFeedback.generateErrorFeedback(this.uuid(), null as any, errorMessage);
    this.store.dispatch(new EffectFeedbackActions.AddEffectFeedback({ effectFeedback }));

    if (openedWindow && !openedWindow.closed) {
      openedWindow.close();
    }
  }
}
